import queryString from 'querystring';

const isFilterCategory = category =>
  /^s/.test(category.id) || category.filterId !== null;

const getParentPath = category =>
  category.path && category.path.length > 0
    ? category.path.slice(-1).pop().pathName
    : category.pathName;

const getPopularBrands = products => {
  const brandsAggregation = products.reduce((brands, product) => {
    if (!product.brand) {
      return brands;
    }

    const brand = product.brand.name;

    return Object.assign(brands, {
      [brand]: (brands[brand] || 0) + 1,
    });
  }, {});

  return Object.keys(brandsAggregation).sort(
    (a, b) => brandsAggregation[b] - brandsAggregation[a]
  );
};

const getUrlsForSEO = (domain, path, pageInfo) => {
  const currentOffset = pageInfo.offset || 0;
  const currentLimit = pageInfo.limit;

  const [pathName, query] = path.split('?');

  const qs = queryString.parse(query || {});

  const prevOffset = Math.max(0, currentOffset - currentLimit);
  const prevParams = {
    ...qs,
    ...(prevOffset > 0 ? { offset: prevOffset } : {}),
  };

  const nextParams = {
    ...qs,
    offset: currentOffset + currentLimit,
  };

  const canonicalParams = {
    ...qs,
    ...(currentOffset > 0 && { offset: currentOffset }),
  };

  return {
    prev:
      domain +
      pathName +
      (Object.keys(prevParams).length > 0
        ? `?${queryString.encode(prevParams)}`
        : ''),
    next:
      domain +
      pathName +
      (Object.keys(nextParams).length > 0
        ? `?${queryString.encode(nextParams)}`
        : ''),
    canonical:
      domain +
      pathName +
      (Object.keys(canonicalParams).length > 0
        ? `?${queryString.encode(canonicalParams)}`
        : ''),
  };
};

const addBrandToCategoryUrl = (category, url) => {
  if (!category.brand) {
    return url;
  }

  if (/[?&]k=/.test(url)) {
    return `${url}&t=${category.brand.id}`;
  }

  if (/--c/.test(url)) {
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    return `${url}${separator}brand=${category.brand.id}`;
  }

  return url;
};

export default ({ data: { category } }, { market, t }) => {
  if (!category) {
    return {};
  }

  const meta = {
    data: category.name,
    name: category.name,
  };

  const categoryNames = [
    category.name,
    ...(category.categories
      ? category.categories.nodes.map(c => c.name).slice(0, 3)
      : []),
  ].join(', ');
  const productCount = category.productCount;
  const popularBrands = getPopularBrands(category.products.nodes)
    .slice(0, 3)
    .join(', ');

  const description = t('meta', 'category.description', {
    categoryNames,
    productCount,
    popularBrands,
  });

  const openGraph = [
    {
      key: 'title',
      content: t('meta', 'category.og-title', {
        name: category.name,
      }),
    },
    { key: 'url', content: market.domain + category.pathName },
    { key: 'desc', content: description },
    { key: 'type', content: 'product.group' },
    { key: 'image', content: category.logo },
  ];

  const isFilter = isFilterCategory(category);
  const path = isFilter ? getParentPath(category) : category.pathName;

  // Will only use these values if its not a filtered result and we are viewing the leaf node in the category tree
  const { prev, canonical, next } =
    !isFilter && category.immediateProducts
      ? getUrlsForSEO(market.domain, path, category.products.pageInfo)
      : {};

  return {
    canonical: canonical || market.domain + path,
    next: category.products.pageInfo.hasNext && next,
    prev: category.products.pageInfo.hasPrevious && prev,
    thumbnail: category.logo,
    description,
    title: t('meta', 'category.title', meta),
    openGraph,
    breadcrumb: category.path,
    structuredData: {
      '@context': 'http://schema.org',
      '@type': 'Thing',
      name: category.name,
      description,
      image: category.logo,
      url: market.domain + category.pathName,
    },
    android: category.pathName,
    ios: `/category/${category.id}`, // TODO: Semantic id when app can handle it
    hrefLang:
      category.hrefLang &&
      category.hrefLang.map(link => ({
        ...link,
        url: addBrandToCategoryUrl(category, link.url),
      })),
  };
};
